import { Head, Link, router } from '@inertiajs/react'
import { PageProps } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import { Banknote, Download, Filter, ArrowRight } from 'lucide-react'

interface PaymentRow {
  id: number
  kind: 'agent_shortfall' | 'sponsor_shortfall'
  status: 'pending' | 'reviewed' | 'paid'
  amount: number
  agent: { id: number; name: string } | null
  carrier: { id: number; name: string; code: string } | null
  commission: { id: number; policy_number: string | null; gross_amount: number; received_amount: number; carrier_payout_percentage: number } | null
  created_at: string
  reviewed_at: string | null
  paid_at: string | null
}

interface Props extends PageProps {
  records: PaymentRow[]
  counts: { pending: number; reviewed: number; paid: number; total: number }
  totals: { pending_amount: number; reviewed_amount: number; paid_amount: number }
  agents: { id: number; name: string }[]
  carriers: { id: number; name: string; code: string }[]
  filters: { agent_id: string | null; carrier_id: string | null; status: string | null }
}

const fmtMoney = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n)

const statusClass = (s: string) =>
  s === 'paid' ? 'bg-forest-green/15 text-forest-green'
  : s === 'reviewed' ? 'bg-gold/30 text-charcoal'
  : 'bg-burgundy/10 text-burgundy'

export default function PaymentsIndex({ records, counts, totals, agents, carriers, filters }: Props) {
  const update = (next: Partial<typeof filters>) => {
    const merged = { ...filters, ...next }
    const params: Record<string, string> = {}
    if (merged.agent_id)   params.agent_id   = merged.agent_id
    if (merged.carrier_id) params.carrier_id = merged.carrier_id
    if (merged.status)     params.status     = merged.status
    router.get('/admin/payments', params, { preserveState: false })
  }

  return (
    <AppLayout>
      <Head title="Payments" />
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading flex items-center gap-3">
              <Banknote className="w-7 h-7 text-forest-green" /> Payments
            </h1>
            <p className="text-charcoal/60 mt-1 text-sm">
              Shortfall disbursements Chestnut owes agents when carriers pay under 100%.
            </p>
          </div>
          <a
            href="/admin/payments/export_reviewed"
            className="px-3 py-2 text-sm bg-forest-green text-cream rounded hover:bg-forest-green/90 inline-flex items-center gap-2"
          >
            <Download className="w-4 h-4" /> Export Reviewed CSV
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {[
            { label: 'Pending',  count: counts.pending,  total: totals.pending_amount,  cls: 'border-burgundy/30' },
            { label: 'Reviewed', count: counts.reviewed, total: totals.reviewed_amount, cls: 'border-gold' },
            { label: 'Paid',     count: counts.paid,     total: totals.paid_amount,     cls: 'border-forest-green/30' },
            { label: 'Total',    count: counts.total,    total: totals.pending_amount + totals.reviewed_amount + totals.paid_amount, cls: 'border-charcoal/20' }
          ].map(s => (
            <div key={s.label} className={`bg-white border ${s.cls} rounded-lg p-4`}>
              <div className="text-xs uppercase tracking-wide text-charcoal/60">{s.label}</div>
              <div className="text-2xl font-bold text-charcoal mt-1">{s.count}</div>
              <div className="text-sm text-charcoal/70 font-mono">{fmtMoney(s.total)}</div>
            </div>
          ))}
        </div>

        <div className="bg-white border border-charcoal/10 rounded-lg p-3 flex flex-wrap gap-3 items-end">
          <div className="flex items-center gap-2 text-sm text-charcoal/60"><Filter className="w-4 h-4" /> Filters</div>
          <div>
            <label className="block text-xs uppercase text-charcoal/60 mb-1">Status</label>
            <select
              value={filters.status || ''}
              onChange={e => update({ status: e.target.value || null })}
              className="border border-charcoal/20 rounded px-2 py-1.5 text-sm"
            >
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="reviewed">Reviewed</option>
              <option value="paid">Paid</option>
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase text-charcoal/60 mb-1">Agent</label>
            <select
              value={filters.agent_id || ''}
              onChange={e => update({ agent_id: e.target.value || null })}
              className="border border-charcoal/20 rounded px-2 py-1.5 text-sm min-w-[180px]"
            >
              <option value="">All</option>
              {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase text-charcoal/60 mb-1">Carrier</label>
            <select
              value={filters.carrier_id || ''}
              onChange={e => update({ carrier_id: e.target.value || null })}
              className="border border-charcoal/20 rounded px-2 py-1.5 text-sm min-w-[180px]"
            >
              <option value="">All</option>
              {carriers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          {(filters.agent_id || filters.carrier_id || filters.status) && (
            <button
              onClick={() => router.get('/admin/payments')}
              className="px-3 py-1.5 text-sm border border-charcoal/20 rounded"
            >
              Clear
            </button>
          )}
        </div>

        <div className="bg-white border border-charcoal/10 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-cream text-left text-xs uppercase tracking-wide text-charcoal/60">
                <tr>
                  <th className="px-3 py-2">Created</th>
                  <th className="px-3 py-2">Agent</th>
                  <th className="px-3 py-2">Kind</th>
                  <th className="px-3 py-2">Carrier</th>
                  <th className="px-3 py-2">Source Commission</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {records.length === 0 && (
                  <tr><td colSpan={8} className="px-3 py-8 text-center text-charcoal/50">No payments match these filters.</td></tr>
                )}
                {records.map(p => (
                  <tr key={p.id} className="border-t border-charcoal/5 hover:bg-cream/40">
                    <td className="px-3 py-2 whitespace-nowrap text-xs">{new Date(p.created_at).toLocaleDateString()}</td>
                    <td className="px-3 py-2">{p.agent?.name || '—'}</td>
                    <td className="px-3 py-2 text-xs">
                      <span className={`px-2 py-0.5 rounded ${p.kind === 'agent_shortfall' ? 'bg-forest-green/10 text-forest-green' : 'bg-gold/30 text-charcoal'}`}>
                        {p.kind === 'agent_shortfall' ? 'Agent' : 'Sponsor'}
                      </span>
                    </td>
                    <td className="px-3 py-2">{p.carrier?.name || '—'} <span className="text-xs text-charcoal/50">{p.carrier?.code}</span></td>
                    <td className="px-3 py-2 text-xs">
                      {p.commission ? (
                        <Link href={`/commissions/${p.commission.id}`} className="text-forest-green hover:underline">
                          #{p.commission.id}{p.commission.policy_number ? ` · ${p.commission.policy_number}` : ''}
                        </Link>
                      ) : '—'}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{fmtMoney(p.amount)}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 text-xs rounded capitalize ${statusClass(p.status)}`}>{p.status}</span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Link href={`/admin/payments/${p.id}`} className="text-forest-green hover:underline text-sm inline-flex items-center gap-1">
                        Open <ArrowRight className="w-3 h-3" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AppLayout>
  )
}
